import { VERIFY_RETURN_TO_KEY } from "@kovernow/flows/kyc";
import { Button } from "@kovernow/react-ui/components/button";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Skeleton } from "@kovernow/react-ui/components/skeleton";
import { useEffect } from "react";
import { Link } from "react-router";
import { useT } from "../../core/use-t";

/**
 * Pieces shared by the SG (W-7, SingPass) and HK (W-8, ShuftiPro) verify
 * screens: the stored `returnTo`, the loading and verified states, and the
 * bfcache busy reset.
 */

export function readStoredReturnTo(): string | null {
  try {
    return window.sessionStorage.getItem(VERIFY_RETURN_TO_KEY);
  } catch {
    return null;
  }
}

export function writeStoredReturnTo(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(VERIFY_RETURN_TO_KEY);
    else window.sessionStorage.setItem(VERIFY_RETURN_TO_KEY, value);
  } catch {
    // Storage unavailable: the return falls back to Profile.
  }
}

/** Loading placeholder: a Skeleton, never a lone spinner, and no button. */
export function VerifyLoading({ label }: { label: string }) {
  return (
    <div aria-busy="true" className="flex flex-col gap-3" data-testid="verify-loading">
      <span className="sr-only">{label}</span>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-11 w-48" />
    </div>
  );
}

/** Already verified: nothing to start, "Continue" to `returnTo`. */
export function VerifiedBanner({ returnTo }: { returnTo: string }) {
  const t = useT();
  return (
    <InfoBanner
      data-testid="verify-verified"
      tone="success"
      title={t("verify.verifiedTitle")}
      body={t("verify.verifiedBody")}
      action={
        <Button asChild variant="outline" size="touch">
          <Link to={returnTo}>{t("verify.continue")}</Link>
        </Button>
      }
    />
  );
}

/**
 * Back from the provider can restore this page from the bfcache with the
 * start button still busy: un-stick it.
 */
export function useBfcacheReset(reset: () => void) {
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) reset();
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, [reset]);
}
